import { Timer } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Button } from 'src/components/Button';
import Checkbox from 'src/components/Checkbox';
import { Input } from 'src/components/Input';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { UpdateEnvironmentDto } from 'src/interfaces/environment.interface';

export const CallPacingControls = () => {
  const { patch } = useAuthedRequest();
  const { environmentData } = useEnvironment();
  const notification = useNotification();
  const [limitCallFrequency, setLimitCallFrequency] = useState(false);
  const [outboundCallRateMinutes, setMinTimeBetweenCallsSeconds] = useState<
    number | null
  >(null);
  const [maxOutboundCallsPerPeriod, setMaxOutboundCallsPerPeriod] = useState<
    number | null
  >(null);
  const [pacingPeriodHours, setPacingPeriodHours] = useState<number | null>(
    null,
  );

  const isValidPacingSettings = (config: {
    outboundCallRateMinutes?: number | null;
    maxOutboundCallsPerPeriod?: number | null;
    pacingPeriodHours?: number | null;
  }) => {
    const {
      outboundCallRateMinutes: rate,
      maxOutboundCallsPerPeriod: maxCalls,
      pacingPeriodHours: hours,
    } = config;
    return (
      typeof rate === 'number' &&
      !isNaN(rate) &&
      rate >= 0 &&
      rate <= 1440 &&
      typeof maxCalls === 'number' &&
      !isNaN(maxCalls) &&
      maxCalls >= 0 &&
      maxCalls <= 100 &&
      typeof hours === 'number' &&
      !isNaN(hours) &&
      hours >= 0 &&
      hours <= 24
    );
  };

  useEffect(() => {
    if (!environmentData) return;
    const limits = environmentData.limits;
    if (isValidPacingSettings(limits)) {
      setLimitCallFrequency(true);
      setMinTimeBetweenCallsSeconds(limits.outboundCallRateMinutes!);
      setMaxOutboundCallsPerPeriod(limits.maxOutboundCallsPerPeriod!);
      setPacingPeriodHours(limits.pacingPeriodHours!);
    } else {
      setLimitCallFrequency(false);
      setMinTimeBetweenCallsSeconds(null);
      setMaxOutboundCallsPerPeriod(null);
      setPacingPeriodHours(null);
    }
  }, [environmentData]);

  useEffect(() => {
    if (!limitCallFrequency) {
      setMinTimeBetweenCallsSeconds(null);
      setMaxOutboundCallsPerPeriod(null);
      setPacingPeriodHours(null);
    }
  }, [limitCallFrequency]);

  const updateCallPacing = async () => {
    if (!environmentData) return;

    const payload: UpdateEnvironmentDto = {
      name: environmentData.name,
      limits: {
        ...environmentData.limits,
        outboundCallRateMinutes,
        maxOutboundCallsPerPeriod,
        pacingPeriodHours,
      },
    };

    try {
      await patch(`/environments/${environmentData.id}`, payload);
      notification.success('Call pacing settings updated successfully');
    } catch (error) {
      console.error(error);
      notification.error('Failed to update call pacing settings');
    }
  };

  return (
    <div className="p-4 border border-neutral-200 rounded-md bg-neutral-50">
      <div className="flex justify-between mb-4">
        <div className="flex items-center">
          <Timer size={32} className="text-primary" />
          <div className="flex flex-col ml-2">
            <h3>{environmentData?.name} - Call pacing settings</h3>
            <span className="text-sm text-neutral-400">
              Avoid spam flags by controlling how often your numbers are used.
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            checked={limitCallFrequency}
            onChange={(checked: boolean) => {
              setLimitCallFrequency(checked);
              if (!checked) {
                setMinTimeBetweenCallsSeconds(null);
                setMaxOutboundCallsPerPeriod(null);
                setPacingPeriodHours(null);
              }
            }}
            ariaLabel="Limit call frequency"
          />
          <span className="text-sm font-title">Limit call frequency</span>
        </div>
      </div>
      {limitCallFrequency && (
        <>
          <div className="flex gap-2 items-center w-full">
            <span>Wait</span>
            <div className="w-fit">
              <Input
                type="number"
                min={0}
                max={1440} // 1 day
                placeholder="e.g. 5"
                value={
                  isNaN(parseInt(`${outboundCallRateMinutes}`, 10))
                    ? ''
                    : `${outboundCallRateMinutes}`
                }
                onChange={(e) => {
                  setMinTimeBetweenCallsSeconds(
                    Number.isNaN(parseInt(e.target.value, 10))
                      ? null
                      : parseInt(e.target.value, 10),
                  );
                }}
                required
              />
            </div>
            <span>minutes between calls for each number</span>
          </div>
          <div className="flex gap-2 mt-2 items-center w-full">
            <span>No more than</span>
            <div className="w-fit">
              <Input
                type="number"
                min={0}
                max={100} // our recommendation per day
                placeholder="e.g. 10"
                value={
                  isNaN(parseInt(`${maxOutboundCallsPerPeriod}`, 10))
                    ? ''
                    : `${maxOutboundCallsPerPeriod}`
                }
                onChange={(e) => {
                  setMaxOutboundCallsPerPeriod(
                    Number.isNaN(parseInt(e.target.value, 10))
                      ? null
                      : parseInt(e.target.value, 10),
                  );
                }}
              />
            </div>
            <span>calls every</span>
            <div className="w-fit">
              <Input
                type="number"
                min={1}
                max={24} // one day
                placeholder="e.g. 1"
                value={
                  isNaN(parseInt(`${pacingPeriodHours}`, 10))
                    ? ''
                    : `${pacingPeriodHours}`
                }
                onChange={(e) => {
                  setPacingPeriodHours(
                    Number.isNaN(parseInt(e.target.value, 10))
                      ? null
                      : parseInt(e.target.value, 10),
                  );
                }}
              />
            </div>
            <span>hour(s) for each number</span>
          </div>
        </>
      )}
      <div className="flex justify-end">
        <Button
          variant="contained"
          color="primary"
          onClick={updateCallPacing}
          disabled={
            limitCallFrequency &&
            !isValidPacingSettings({
              outboundCallRateMinutes,
              maxOutboundCallsPerPeriod,
              pacingPeriodHours,
            })
          }
        >
          Save
        </Button>
      </div>
    </div>
  );
};
